import React from 'react';
import { useTranslation } from 'react-i18next';
import { Campaign } from '../../../shared/types/Campaign';
import { TagIcon, PercentIcon, DollarSignIcon } from 'lucide-react';
interface CampaignListProps {
  campaigns: Campaign[];
  onCampaignClick: (campaignId: string) => void;
}
export function CampaignList({
  campaigns,
  onCampaignClick
}: CampaignListProps) {
  const {
    t
  } = useTranslation('shops');
  // Function to calculate budget usage percentage
  const calculateBudgetUsage = (campaign: Campaign) => {
    if (campaign.budget === 0) return 0;
    return Math.round(campaign.budgetUsed / campaign.budget * 100);
  };
  // Function to determine status badge styling
  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      active: 'bg-green-50 text-green-700 border border-green-100',
      inactive: 'bg-gray-100 text-gray-600 border border-gray-200',
      expired: 'bg-red-50 text-red-700 border border-red-100'
    };
    const style = styles[status] || styles.inactive;
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium ${style}`} data-id="element-10489">
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>;
  };
  return <table className="w-full text-left border-collapse" data-id="element-10490">
      <thead data-id="element-10491">
        <tr className="border-b border-gray-100" data-id="element-10492">
          <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10493">
            {t('components.CampaignList.campaign')}
          </th>
          <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10494">
            {t('components.CampaignList.code_strategy')}
          </th>
          <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10495">
            {t('components.CampaignList.discount')}
          </th>
          <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10496">
            {t('components.CampaignList.budget_usage')}
          </th>
          <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10497">
            {t('components.CampaignList.expiry_date')}
          </th>
          <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider text-right" data-id="element-10498">
            {t('components.CampaignList.status')}
          </th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100" data-id="element-10499">
        {campaigns.length > 0 ? campaigns.map(campaign => <tr key={campaign.id} onClick={() => onCampaignClick(campaign.id)} className="hover:bg-gray-50 cursor-pointer transition-colors group" data-id="element-10500">
              <td className="px-6 py-4" data-id="element-10501">
                <div className="text-sm font-medium text-gray-900 group-hover:text-[#9F140F] transition-colors" data-id="element-10502">
                  {campaign.title}
                </div>
                <div className="text-sm text-gray-500 mt-0.5" data-id="element-10503">
                  {campaign.category}
                </div>
              </td>
              <td className="px-6 py-4" data-id="element-10504">
                <div className="text-sm text-gray-900" data-id="element-10505">
                  {campaign.codeStrategy === 'Global' ? <span className="font-medium" data-id="element-10506">{campaign.code}</span> : <span data-id="element-10507">{t('components.CampaignList.generated_codes')}</span>}
                </div>
                <div className="text-sm text-gray-500 mt-0.5" data-id="element-10508">
                  {campaign.codeStrategy}
                </div>
              </td>
              <td className="px-6 py-4" data-id="element-10509">
                <div className="flex items-center text-sm font-medium text-gray-900" data-id="element-10510">
                  {campaign.discountPercentage > 0 ? <>
                      <PercentIcon size={14} className="mr-1 text-gray-400" data-id="element-10511" />
                      {campaign.discountPercentage}%
                    </> : <>
                      <DollarSignIcon size={14} className="mr-1 text-gray-400" data-id="element-10512" />
                      {campaign.fixedDiscountAmount} {campaign.currency}
                    </>}
                </div>
                <div className="text-sm text-gray-500 mt-0.5" data-id="element-10513">
                  {t('components.CampaignList.carried_by')}{' '}
                  {campaign.discountCarriedBy}
                </div>
              </td>
              <td className="px-6 py-4" data-id="element-10514">
                <div className="flex items-center gap-2" data-id="element-10515">
                  <div className="w-24 bg-gray-100 rounded-full h-1.5 overflow-hidden" data-id="element-10516">
                    <div className="bg-[#E2231A] h-1.5 rounded-full transition-all" style={{
                width: `${calculateBudgetUsage(campaign)}%`
              }} data-id="element-10517"></div>
                  </div>
                  <span className="text-sm font-medium text-gray-900" data-id="element-10518">
                    {calculateBudgetUsage(campaign)}%
                  </span>
                </div>
                <div className="text-sm text-gray-500 mt-0.5" data-id="element-10519">
                  {campaign.budgetUsed.toLocaleString()} /{' '}
                  {campaign.budget.toLocaleString()} {campaign.currency}
                </div>
              </td>
              <td className="px-6 py-4" data-id="element-10520">
                <div className="text-sm text-gray-900" data-id="element-10521">
                  {campaign.expiryDate}
                </div>
              </td>
              <td className="px-6 py-4 text-right" data-id="element-10522">
                {getStatusBadge(campaign.status)}
              </td>
            </tr>) : <tr data-id="element-10523">
            <td colSpan={6} className="px-6 py-12 text-center text-gray-500 text-sm" data-id="element-10524">
              {t('components.CampaignList.no_campaigns_found_matching_your_criteria')}
            </td>
          </tr>}
      </tbody>
    </table>;
}